import React from 'react';
import dayjs from 'dayjs';
import {Space, Tooltip} from 'antd';
import {LoadingOutlined} from '@ant-design/icons';
import {Link} from 'react-router-dom';
import {
  StyledJournalEntriesTabsIconSpace,
  StyledJournalEntriesTabsDatePicker,
  StyledJournalEntriesTabsInput,
  StyledJournalEntriesTabsSelect
} from '../../components/pages/NetSuiteJournalEntriesPage/JournalEntriesTabs/StyledJournalEntriesTabs';
import {
  StyledBillMatchedSwap,
  StyledMissingErpTabIcon,
  StyledMissingErpTabIconsSpace
} from '../../components/pages/NetSuiteBillsPage/tabs/MissingErpTab/StyledMissingErpTab';
import {SuggestionTableInput, SuggestionTableSelect} from '../../components/SuggestionTableField';
import IconWrapper from '../../components/IconWrapper';
import MatchedBadge from '../../components/MatchedBadge';
import MissingInfo from '../../components/MissingInfo';
import WarningTooltipIcon from '../../components/WarningTooltipIcon';
import StatusMark from '../../components/StatusMark';
import UserDetails from '../../components/UserDetails';
import ScaleFade from '../../components/animations/ScaleFade';
import InvoicePreviewWithIcon from '../../components/pages/NetSuiteJournalEntriesPage/InvoicePreviewWithIcon';
import {
  billInvoiceSyncStatusConstants,
  customFieldTypeConstants,
  customSegmentLevelTypeConstants,
  expenseTypesConstants,
  journalEntryLevelConstants,
  journalEntryStatusesConstants,
  journalEntrySyncStatusConstants,
  suggestionRecordTypeConstants,
  vendorSyncStatusConstants
} from '../../constants';
import {alertActions} from '../../state/actions/alert.actions';
import {
  getHeaderCreditAccount,
  getHeaderSegmentValue,
  getHeaderUpdateLevels,
  getDisplayChildren,
  getMembers,
  formatMember,
  isAggregateChildRow,
  isAggregateHeader,
  isAggregateMember,
  isSelectableMember,
  isHeaderReadyToSync,
  isLocationLineLevel,
} from './aggregateJournalEntry.helpers';
import {amountHelpers} from './amount.helpers';
import {formHelpers} from './form.helpers';
import {dateHelpers} from './date.helpers';
import {expensesHelpers} from './expenses.helpers';
import {invoicesHelpers} from './invoices.helpers';
import {objectHelpers} from './object.helpers';
import {tabHelpers} from './tab.helpers';
import {teamsHelpers} from './teams.helpers';
import {textHelpers} from './text.helpers';
import {vendorsHelpers} from './vendors.helpers';
import {AlertTriangleIcon, CheckIcon, CloseIcon, ExternalLinkIcon, FileInvoiceIcon, WithLessCreditCardIcon} from '../../icons';
import {colorsStyles} from '../styles';

const {READY_FOR_SUBMISSION, READY_FOR_REVIEW} = journalEntryStatusesConstants;

const journalEntrySyncStatuses = {
  [journalEntrySyncStatusConstants.IN_PROGRESS]: {
    key: 'journalEntrySyncStatuses.inProgress',
    color: '#FFCC00'
  },
  [journalEntrySyncStatusConstants.ERROR]: {
    key: 'journalEntrySyncStatuses.error',
    color: '#FF4D4F'
  },
  [journalEntrySyncStatusConstants.SUCCESS]: {
    key: 'journalEntrySyncStatuses.success',
    color: '#52C41A'
  },
};

const vendorSyncStatuses = {
  [vendorSyncStatusConstants.ERROR]: {
    key: 'vendorSyncStatuses.error',
    color: '#FF4D4F'
  },
  [vendorSyncStatusConstants.IN_PROGRESS]: {
    key: 'vendorSyncStatuses.inProgress',
    color: '#FFCC00'
  },
  [vendorSyncStatusConstants.SUCCESS]: {
    key: 'vendorSyncStatuses.success',
    color: '#52C41A'
  },
};

export const SyncStatusOptions = () => formHelpers.getStatusOptionsList(vendorSyncStatuses);

// A cent of drift is normal 2-decimal rounding noise, not a real mismatch — same convention as
// InvoiceLineItemsTable's RECONCILE_TOLERANCE.
const BILL_SPLIT_RECONCILE_TOLERANCE = 0.01;

export const accountingHelpers = {

  /**
   * Returns suggestion lookup data and whether the suggestion matches the current default value.
   * @param {Object} params - Parameters object.
   * @param {*} params.defaultValue - Current field value to compare against.
   * @param {string} params.propName - Field name used as the default suggestion key.
   * @param {Object} params.record - Table row record containing agent_suggestion data.
   * @param {string} [params.suggestionPropName] - Optional override key for suggestion lookup.
   * @returns {{suggestionKey: string, suggestion: Object|undefined, hasSuggestionValue: boolean}} Suggestion data and match flag.
   * @example
   * const { suggestion, hasSuggestionValue } = accountingHelpers.getSuggestionState({
   *   defaultValue: 'foo',
   *   propName: 'memo',
   *   record,
   * });
   */
  getSuggestionState: ({defaultValue, propName, record, suggestionPropName}) => {
    const suggestionKey = suggestionPropName || propName;
    const suggestion = record?.agent_suggestion?.suggestions?.[suggestionKey];
    const isSuggestionValueDefined = Boolean(suggestion);
    const hasSuggestionValue = isSuggestionValueDefined && (objectHelpers.isEmptyValue(defaultValue) || suggestion?.value === defaultValue);
     return {
       suggestionKey,
       suggestion,
       hasSuggestionValue
     };
  },

  /**
   * Returns Ant Design column props for rendering a sortable date column.
   * @returns {{render: Function, sortDirections: string[], sorter: boolean, width: number}} Column configuration object.
   * @example
   * const col = accountingHelpers.getDateColumnProps();
   */
  getDateColumnProps: () => ({
    render: (value) => value ? dateHelpers.getDateWithMonth(value, 'YYYY-MM-DD') : '',
    sortDirections: ['descend', 'ascend'],
    sorter: true,
    width: 150,
  }),

  /**
   * Returns Ant Design column props that cap the cell width and enable ellipsis truncation,
   * shared across accounting tables for editable input/select columns.
   * @param {number} [maxWidth=200] - Maximum cell width in pixels.
   * @returns {{onCell: Function, ellipsis: boolean}} Column configuration object.
   * @example
   * const col = { dataIndex: 'memo', ...accountingHelpers.getEllipsisColumnProps() };
   */
  getEllipsisColumnProps: (maxWidth = 200) => ({
    onCell: () => ({ style: { maxWidth } }),
    ellipsis: true,
  }),

  /**
   * Returns a span element containing the formatted bill invoice date.
   * @param {string} value - Raw date string in YYYY-MM-DD format.
   * @returns {React.ReactElement} A span with a no-wrap class containing the formatted date or empty string.
   * @example
   * accountingHelpers.getBillInvoiceDate('2024-01-15');
   */
  getBillInvoiceDate: (value) => {
    const date = value ? dateHelpers.getDateWithMonth(value, 'YYYY-MM-DD') : '';
    return <span className='no-wrap'>{date}</span>;
  },

  /**
   * Returns a Space element with an external link icon for the given invoice.
   * @param {Object} [params={}] - Parameters object.
   * @param {Object} [params.invoice] - Invoice object with backend_invoice_id.
   * @param {Function} [params.t] - i18n translation function.
   * @returns {React.ReactElement} Ant Design Space with an invoice link icon.
   * @example
   * accountingHelpers.getBillInvoiceLink({ invoice: { backend_invoice_id: '123' }, t });
   */
  getBillInvoiceLink: ({invoice, t} = {}) => (
    <Space className='ant-table-cell-actions' size='small' wrap={false}>
      {accountingHelpers.getInvoiceLinkIcon({id: invoice?.backend_invoice_id, t})}
    </Space>
  ),

  /**
   * Returns a Space element combining the bill invoice date and its external link icon.
   * @param {Object} [params={}] - Parameters object.
   * @param {Object} [params.invoice] - Invoice object.
   * @param {Function} [params.t] - i18n translation function.
   * @returns {React.ReactElement} Space element with date and link icon.
   * @example
   * accountingHelpers.getBillInvoiceDateWithLink({ invoice, t });
   */
  getBillInvoiceDateWithLink: ({invoice, t} = {}) => (
    <StyledMissingErpTabIconsSpace size='middle' wrap={false}>
      {accountingHelpers.getBillInvoiceDate(invoice?.invoice_date)}
      {accountingHelpers.getBillInvoiceLink({invoice, t})}
    </StyledMissingErpTabIconsSpace>
  ),

  /**
   * Returns a Space element combining the vendor bill reference number and its external link icon.
   * @param {Object} [params={}] - Parameters object.
   * @param {Object} [params.invoice] - Invoice object with custbody_nexil_numb_vendorbillrefnum.
   * @param {Function} [params.t] - i18n translation function.
   * @returns {React.ReactElement} Space element with reference number and link icon.
   * @example
   * accountingHelpers.getBillInvoiceNumberWithLink({ invoice, t });
   */
  getBillInvoiceNumberWithLink: ({invoice, t} = {}) => (
    <StyledMissingErpTabIconsSpace size='middle' wrap={false}>
      <span className='no-wrap'>{invoice?.custbody_nexil_numb_vendorbillrefnum || ''}</span>
      {accountingHelpers.getBillActionsWithMatched({
        record: invoice,
        t,
        actions: accountingHelpers.getInvoiceLinkIcon({id: invoice?.backend_invoice_id, t}),
      })}
    </StyledMissingErpTabIconsSpace>
  ),

  /**
   * Stacks the row's hover action icons and the "Matched" badge in the same slot so the
   * actions replace the badge in place on row hover. The badge shows while the row is not
   * hovered (when the bill is matched); the actions fade in over it on hover. Opacity swap
   * is handled by tableStyles.cellActionsHover via `.ant-table-cell-actions` /
   * `.ant-table-cell-matched`.
   * @param {Object} [params={}] - Parameters object.
   * @param {Object} [params.record] - Bill row; the badge renders only when record.expense_id is set.
   * @param {Function} [params.t] - i18n translation function.
   * @param {React.ReactNode} [params.actions] - Hover action nodes (icons/buttons).
   * @returns {React.ReactElement} Grid-stacked actions and matched badge.
   * @example
   * accountingHelpers.getBillActionsWithMatched({ record, t, actions: <LinkIcon /> });
   */
  getBillActionsWithMatched: ({record, t, actions} = {}) => (
    <StyledBillMatchedSwap>
      <Space className='ant-table-cell-actions' size='small' wrap={false}>
        {actions}
      </Space>
      {record?.expense_id ? (
        <span className='ant-table-cell-matched'>
          <MatchedBadge tooltipTitle={t('vendorBillMatchedTooltip')} />
        </span>
      ) : null}
    </StyledBillMatchedSwap>
  ),

  /**
   * Returns the tax code from the first item of a bill's item array.
   * @param {Object} data - Bill data object with an item array.
   * @returns {string} Tax code string or empty string if unavailable.
   * @example
   * accountingHelpers.getBillTaxCode({ item: [{ tax_code: 'VAT20' }] }); // 'VAT20'
   */
  getBillTaxCode: (data) => Array.isArray(data?.item) && data.item.length ? data.item[0]?.tax_code : '',

  /**
   * Returns the item value from the first element of a bill's item array.
   * @param {Object} data - Bill data object with an item array.
   * @returns {*} The item value or undefined if unavailable.
   * @example
   * accountingHelpers.getBillListItem({ item: [{ item: 'SRV-001' }] }); // 'SRV-001'
   */
  getBillListItem: (data) => Array.isArray(data?.item) && data.item.length ? data.item[0]?.item : undefined,

  /**
   * Returns the vendor name from a table record.
   * @param {*} _ - Unused cell value parameter (Ant Design table render pattern).
   * @param {Object} record - Table row record containing a vendor object.
   * @returns {string} The vendor name or empty string.
   * @example
   * accountingHelpers.getBillVendorName(null, { vendor: { name: 'Acme Corp' } }); // 'Acme Corp'
   */
  getBillVendorName: (_, record) => record?.vendor?.name || '',

  /**
   * Returns the account from the first journal entry line that has a credit amount greater than zero.
   * @param {Object} data - Journal entry data with a lines array.
   * @returns {*|null} The credit account value or null if not found.
   * @example
   * accountingHelpers.getCreditAccount({ lines: [{ line_credit: '100', account: 'ACC-01' }] });
   */
  getCreditAccount: (data) => {
    const line = (data?.lines || []).find(i => Number(i.line_credit) > 0);
    return line?.account ?? null;
  },

  /**
   * Returns the account from the first journal entry line that has a debit amount greater than zero.
   * @param {Object} data - Journal entry data with a lines array.
   * @returns {*|null} The debit account value or null if not found.
   * @example
   * accountingHelpers.getDebitAccount({ lines: [{ line_debit: '200', account: 'ACC-02' }] });
   */
  getDebitAccount: (data) => {
    const line = (data?.lines || []).find(i => Number(i.line_debit) > 0);
    return line?.account ?? null;
  },

  /**
   * Returns the department from the first journal entry line.
   * @param {Object} data - Journal entry data with a lines array.
   * @returns {*|null} The department value or null if not present.
   * @example
   * accountingHelpers.getDepartment({ lines: [{ department: 'Engineering' }] });
   */
  getDepartment: (data) => {
    return data?.lines?.[0]?.department ?? null;
  },

  /**
   * Returns the credit or debit amount value from the first journal entry line.
   * @param {Object} data - Journal entry data with a lines array.
   * @returns {number|string} The line_credit, line_debit, or 0 if neither is present.
   * @example
   * accountingHelpers.getEntryAmountValue({ lines: [{ line_credit: 150, line_debit: 0 }] }); // 150
   */
  getEntryAmountValue: (data) => {
    // A header's lines carry no amounts until its sync starts, and a member has no lines.
    if (isAggregateHeader(data) || isAggregateMember(data)) return data.amount ?? 0;
    const firstLine = data?.lines?.[0] || {};
    // The API sends amounts as strings, and "0" is truthy: compare them as numbers.
    return Number(firstLine.line_credit) || Number(firstLine.line_debit) || 0;
  },

  /**
   * Returns a formatted amount span element using the journal entry currency and line amount.
   * @param {Object} data - Journal entry data with currency and lines array.
   * @param {Object} [options]
   * @param {string|null} [options.currencyCode] - Currency to show instead of the entry's own
   *   `currency` (see `useJeAmountCurrency`). Only the symbol changes, never the figure.
   * @returns {React.ReactElement} A span with the formatted amount.
   * @example
   * accountingHelpers.getEntryAmount({ currency: 'EUR', lines: [{ line_credit: 100 }] });
   */
  getEntryAmount: (data, {currencyCode} = {}) => {
    const currency = currencyCode || data?.currency || '';
    const amount = accountingHelpers.getEntryAmountValue(data);
    return (
      <span className='amount'>
        {amountHelpers.getAmountWithCurrencyCode(amount, {currencyCode: currency})}
      </span>
    );
  },

  /**
   * Returns a formatted amount span element for a bill using payment or total amount and currency.
   * @param {*} _ - Unused cell value parameter.
   * @param {Object} data - Bill data with payment_currency, currency, total_payment_amount, total_amount.
   * @returns {React.ReactElement} A span with the formatted bill amount.
   * @example
   * accountingHelpers.getBillAmount(null, { payment_currency: 'USD', total_payment_amount: 500 });
   */
  getBillAmount: (_, data) => {
    const currency = data?.payment_currency ?? data?.currency ?? '';
    const amount = data?.total_payment_amount ?? data?.total_amount ?? 0;
    return (
      <span className='amount'>
        {amountHelpers.getAmountWithCurrencyCode(amount, {currencyCode: currency})}
      </span>
    );
  },

  /**
   * Returns a formatted amount span element for a bill's base (pre-VAT) amount and currency.
   * @param {*} _ - Unused cell value parameter.
   * @param {Object} data - Bill data with payment_currency, currency, total_amount.
   * @returns {React.ReactElement} A span with the formatted base amount.
   * @example
   * accountingHelpers.getBillBaseAmount(null, { payment_currency: 'USD', total_amount: 500 });
   */
  getBillBaseAmount: (_, data) => {
    const currency = data?.payment_currency ?? data?.currency ?? '';
    const amount = data?.total_amount ?? 0;
    return (
      <span className='amount'>
        {amountHelpers.getAmountWithCurrencyCode(amount, {currencyCode: currency})}
      </span>
    );
  },

  /**
   * Returns a formatted amount span element for a bill's VAT amount and currency.
   * @param {*} _ - Unused cell value parameter.
   * @param {Object} data - Bill data with payment_currency, currency, total_tax_amount.
   * @returns {React.ReactElement} A span with the formatted VAT amount.
   * @example
   * accountingHelpers.getBillVatAmount(null, { payment_currency: 'USD', total_tax_amount: 50 });
   */
  getBillVatAmount: (_, data) => {
    const currency = data?.payment_currency ?? data?.currency ?? '';
    const amount = data?.total_tax_amount ?? 0;
    return (
      <span className='amount'>
        {amountHelpers.getAmountWithCurrencyCode(amount, {currencyCode: currency})}
      </span>
    );
  },

  /**
   * Compares a split bill's backend_total_amount against the sum of the group's line items (the bill
   * itself is line 0 and sdi_lines holds the remaining lines), returning a
   * matching/mismatch reconciliation message for the split-bill group header, with a warning icon
   * when the totals don't add up.
   * @param {Object} params
   * @param {Object} params.record - The bill record (a split group's first member), carrying backend_total_amount, total_payment_amount/total_amount (numbers or numeric strings), currency/payment_currency, and sdi_lines.
   * @param {Function} params.t - The 'accounting' namespace translation function.
   * @returns {React.ReactElement} The reconciliation message.
   * @example
   * accountingHelpers.getBillSplitReconciliationLabel({ record: bill, t: tAccounting });
   */
  getBillSplitReconciliationLabel: ({record, t}) => {
    const currency = record?.payment_currency ?? record?.currency ?? '';
    const format = (value) => amountHelpers.getAmountWithCurrencyCode(value, {currencyCode: currency});

    const getTotalPaymentAmount = (item) => Number(item?.total_payment_amount ?? item?.total_amount ?? 0);
    const lineItemsAmount = [record, ...(record?.sdi_lines || [])].reduce(
      (sum, line) => sum + getTotalPaymentAmount(line),
      0
    );
    // Without a backend total there is nothing to reconcile against, so it can't mismatch.
    const totalAmount = Number(record?.backend_total_amount ?? lineItemsAmount);
    const missingAmount = totalAmount - lineItemsAmount;
    const hasMismatch = Math.abs(missingAmount) > BILL_SPLIT_RECONCILE_TOLERANCE;

    const message = hasMismatch
      ? t('billSplitReconciliationMismatch', {
          totalAmount: format(totalAmount),
          lineItemsAmount: format(lineItemsAmount),
          missingAmount: format(Math.abs(missingAmount)),
        })
      : t('billSplitReconciliationMatch', {totalAmount: format(totalAmount)});

    return (
      <Space align='center' size={4}>
        {hasMismatch && <WarningTooltipIcon title={message} />}
        {message}
      </Space>
    );
  },

  /**
   * Renders a memo cell value truncated to 60 characters with a tooltip showing the full text when truncated.
   * @param {*} value - The memo value to display.
   * @returns {*|string|React.ReactElement} Original value, plain string, or a Tooltip-wrapped truncated span.
   * @example
   * accountingHelpers.getMemoCell('Short memo'); // 'Short memo'
   * accountingHelpers.getMemoCell('A very long memo that exceeds sixty characters in total length here'); // <Tooltip ...>
   */
  getMemoCell: (value) => textHelpers.cutStringWithTooltip(value, 60),

  /**
   * Returns a Space element with a card icon and card ID, or falsy if no value.
   * @param {string|number} value - Card ID value.
   * @returns {React.ReactElement|undefined} Space with card icon and ID, or undefined if no value.
   * @example
   * accountingHelpers.getEntryCardId('CARD-42');
   */
  getEntryCardId: (value) => value && (
    <StyledJournalEntriesTabsIconSpace size='small' wrap={false} className='justify-start'>
      <WithLessCreditCardIcon /><span>{value}</span>
    </StyledJournalEntriesTabsIconSpace>
  ),

  /**
   * Returns the value of a custom segment field from a journal entry record based on level configuration.
   * @param {Object} [params={}] - Parameters object.
   * @param {string} [params.linesPropName='lines'] - Property name for the lines array on the record.
   * @param {string|string[]} [params.levels=[]] - Level(s) indicating where to read the value (LINES or HEADER).
   * @param {string} params.name - The field name (internal_id) to read.
   * @param {Object} params.record - The journal entry record object.
   * @returns {*} The field value or undefined.
   * @example
   * accountingHelpers.getValue({ levels: ['header'], name: 'class', record: entry });
   */
  getValue: ({linesPropName = 'lines', levels = [], name, record} = {}) => {
    if (isAggregateHeader(record)) return getHeaderSegmentValue({levels, name, record});
    // A member's line-level segments are read from its debit line.
    if (isAggregateMember(record)) return record.line_segments?.[name] || undefined;

    const level = Array.isArray(levels) ? levels[0] : levels;

    if (level === journalEntryLevelConstants.LINES) {
      return record?.[linesPropName]?.[0]?.custom_segments?.[name] || undefined;
    }
    if (level === journalEntryLevelConstants.HEADER) {
      return record?.custom_segments?.[name] || undefined;
    }

    return record?.[name] || undefined;
  },

  /**
   * Builds the update payload for a journal entry field based on the level configuration.
   * @param {Object} [params={}] - Parameters object.
   * @param {Object} [params.entry={}] - Existing journal entry object.
   * @param {string[]} [params.levels=[]] - Levels at which to apply the update (HEADER, LINES, or empty for root).
   * @param {string} params.propName - Property name to update.
   * @param {*} params.value - New value for the property.
   * @returns {Object} Payload object for the update request.
   * @example
   * accountingHelpers.getUpdateData({ entry, levels: ['header'], propName: 'class', value: 'Class-1' });
   */
  getUpdateData: ({entry = {}, levels = [], linesPropName = 'lines', propName, value} = {}) => {
    let data = levels.length === 0 ? { [propName]: value } : {};

    const levelsData = {
      [journalEntryLevelConstants.HEADER]: {
        header_custom_segments: {
          ...(entry?.custom_segments || {}),
          [propName]: value,
        }
      },
      [journalEntryLevelConstants.LINES]: {
        lines_custom_segments: {
          ...(entry?.[linesPropName]?.[0]?.custom_segments || {}),
          [propName]: value,
        }
      }
    };

    // A header has no lines of its own to write: a segment edit goes to the header level only.
    const writeLevels = isAggregateHeader(entry) ? getHeaderUpdateLevels(levels) : levels;
    writeLevels.forEach(level => {
      data = {
        ...data,
        ...(levelsData[level] || {})
      }
    });

    return data;
  },

  /**
   * Builds the update payload for a journal entry field. Delegates to getUpdateData with linesPropName set to 'lines'.
   * @param {Object} [params={}] - Same parameters as getUpdateData, excluding linesPropName.
   * @param {Object} [params.entry={}] - Existing journal entry object.
   * @param {string[]} [params.levels=[]] - Levels at which to apply the update (HEADER, LINES, or empty for root).
   * @param {string} params.propName - Property name to update.
   * @param {*} params.value - New value for the property.
   * @returns {Object} Payload object for the update request.
   * @example
   * accountingHelpers.getJEUpdateData({ entry, levels: ['lines'], propName: 'class', value: 'Class-1' });
   */
  getJEUpdateData: (params = {}) =>
    accountingHelpers.getUpdateData({ ...params, linesPropName: 'lines' }),

  /**
   * Builds the update payload for a vendor bill field. Delegates to getUpdateData with linesPropName set to 'item'.
   * @param {Object} [params={}] - Same parameters as getUpdateData, excluding linesPropName.
   * @param {Object} [params.entry={}] - Existing vendor bill object.
   * @param {string[]} [params.levels=[]] - Levels at which to apply the update (HEADER, LINES, or empty for root).
   * @param {string} params.propName - Property name to update.
   * @param {*} params.value - New value for the property.
   * @returns {Object} Payload object for the update request.
   * @example
   * accountingHelpers.getInvoiceUpdateData({ entry, levels: ['lines'], propName: 'class', value: 'Class-1' });
   */
  getInvoiceUpdateData: (params = {}) =>
    accountingHelpers.getUpdateData({ ...params, linesPropName: 'item' }),

  /**
   * Returns a Space element with a ready-to-sync icon and the formatted transaction date.
   * @param {string} value - Raw date string in YYYY-MM-DD format.
   * @param {Object} record - Journal entry record used to determine ready-to-sync state.
   * @returns {React.ReactElement} Space with sync icon and date.
   * @example
   * accountingHelpers.getEntryTransactionDate('2024-03-01', entry);
   */
  getEntryTransactionDate: (value, record) => (
    <Space size='small' wrap={false} className='d-flex align-items-center'>
      {accountingHelpers.renderReadyToSyncIcon(record)}
      <span className='no-wrap'>
        {dateHelpers.getDateWithMonth(value, 'YYYY-MM-DD')}
      </span>
    </Space>
  ),

  /**
   * Returns a UserDetails component for the employee matching the given ID.
   * @param {Object} [params={}] - Parameters object.
   * @param {string} params.value - Employee ID to look up.
   * @param {Array<Object>} [params.employees=[]] - List of employee objects.
   * @returns {React.ReactElement|string} UserDetails component or empty string if not found.
   * @example
   * accountingHelpers.getOwnerDetails({ value: 'emp-1', employees });
   */
  getOwnerDetails: ({value, employees = []} = {}) => {
    const owner = employees.find(e => e.employee_id === value);
    if (!owner) return '';
    return <UserDetails email={false} user={owner} avatarProps={{size: 24}} />;
  },

  /**
   * Returns true when a journal entry has a linked expense that has not been loaded yet.
   * The expense is fetched in a separate batch request after the entries load, so
   * `record.expense` stays `null` (while `record.expense_id` is set) until it arrives.
   * @param {Object} record - Journal entry record.
   * @returns {boolean} True while the linked expense is still loading.
   * @example
   * accountingHelpers.isExpenseLoading({ expense_id: '42', expense: null }); // true
   */
  isExpenseLoading: (record) => Boolean(record?.expense_id) && !record?.expense,

  /**
   * Renders the expense reporter (`record.expense.reported_by`), showing a spinner
   * while the linked expense is still loading.
   * @param {Object} params
   * @param {Object} params.record - Journal entry record containing the nested expense.
   * @param {Object} [params.avatarProps] - Props forwarded to the avatar.
   * @param {boolean} [params.avatarOnly=false] - When true, renders only the avatar (no name).
   * @returns {React.ReactElement|string} Spinner, UserDetails/avatar element, or '' when no reporter.
   */
  getExpenseReportedBy: ({record, avatarProps = {size: 24}, avatarOnly = false} = {}) => {
    if (accountingHelpers.isExpenseLoading(record)) return <LoadingOutlined spin />;
    const user = record?.expense?.reported_by;
    if (!user) return '';
    return avatarOnly
      ? teamsHelpers.getUserAvatar(user, avatarProps)
      : <UserDetails email={false} user={user} avatarProps={avatarProps} />;
  },

  /**
   * Renders the payment method for a journal entry's linked expense, showing a spinner
   * while the expense is still loading.
   * @param {Object} params
   * @param {Object} params.record - Journal entry record containing the nested expense.
   * @param {Function} params.t - i18n translation function.
   * @returns {React.ReactElement|string|null} Spinner, payment method element, or null.
   */
  getExpensePaymentMethodCell: ({record, t} = {}) => {
    if (accountingHelpers.isExpenseLoading(record)) return <LoadingOutlined spin />;
    return expensesHelpers.getExpensePaymentMethod(record?.expense, t);
  },

  /**
   * Returns a debounced borderless input component for use inside a journal entries table cell.
   * @param {Object} params - Parameters object.
   * @param {*} params.defaultValue - Default input value.
   * @param {Function} params.onChange - Callback invoked with the debounced value.
   * @param {string} params.propName - Field name used to build the placeholder.
   * @param {string} [params.placeholderPropName] - Override for the placeholder translation key.
   * @param {Object} params.record - Table row record.
   * @param {Function} params.t - i18n translation function.
   * @returns {React.ReactElement} Styled debounced input element.
   * @example
   * accountingHelpers.getTableInput({ defaultValue: '', onChange: fn, propName: 'memo', record, t });
   */
  /**
   * Would the accounting service accept this value for a field of this declared type?
   *
   * Returns `true` when it would, `false` when it would answer 400. Never guesses: the rule below
   * is the MEASURED behaviour of the service, not an idea about what a number ought to look like.
   *
   * ⚠ MEASURED, dev, 2026-09-02 (`.e2e/tools/number-field-contract.js`). A `number`-declared field
   * validates by parsing but STORES THE ORIGINAL STRING — `"0410"` comes back `"0410"`, leading zero
   * intact, which is what makes it usable for an ERP account code:
   *
   *   accepted  7 · 0410 · 00 · 0 · -5 · +7 · 3.14 · -0.5 · .5 · 5. · 1e5 · 1E5 · Infinity · NaN
   *             9007199254740993 · 000000000000000000001 · " 7 " · "7\t"   (padding is tolerated)
   *   refused   1,5 · 1 000 · 1.000,50 · 1,000.50 · 0x1F · 7a · 04-A · 12.5.3
   *
   * That set is exactly Python's `float()`. `text` accepts everything.
   *
   * ⚠ DO NOT REACH FOR `Number()`. It is a DIFFERENT rule and wrong in the direction that hurts:
   * `Number('0x1F')` is 31, so it would pass a value the service refuses (harmless — the service
   * still says no, and now says which field), but `Number.isNaN(Number('NaN'))` is true, so it would
   * BLOCK a value the service accepts. Refusing something the server would have taken leaves the
   * user no way to enter it, which is the harm H1MVP-4806 was about. Where the two rules could
   * disagree, this errs toward letting the service decide (NS-5).
   *
   * @param {string} fieldType - The definition's `field_type` (`text`, `number`, `dropdown`).
   * @param {*} value - The raw value as typed. Empty is "no value", not an invalid one.
   * @returns {boolean}
   * @example
   * accountingHelpers.isCustomFieldValueAcceptable('number', '0410'); // true
   * accountingHelpers.isCustomFieldValueAcceptable('number', '04-A'); // false
   */
  isCustomFieldValueAcceptable: (fieldType, value) => {
    // ⚠ WHITESPACE IS NOT "NOTHING TO WRITE". This used to return true for `" "` on the stated
    // premise that the caller omits a blank — it does not: every filter downstream tests `=== ''`,
    // so a single space survived all of them and was PATCHed. And a custom-field value can never be
    // cleared (`""` → 200 ignored, `null` → 422, `DELETE` → 405), so a space pasted from a blank
    // spreadsheet cell over a bulk selection replaced real ERP account codes permanently, with a
    // success toast. Refusing it here is the guard; `apply()` also drops it, because a rule that
    // depends on a caller behaving is not a guard.
    const text = String(value ?? '').trim();
    if (String(value ?? '') !== '' && !text) return false;
    if (fieldType !== customFieldTypeConstants.NUMBER) return true;
    if (!text) return true;
    // Python float grammar: optional sign, digits with an optional point on either side, optional
    // exponent — plus the special literals `float()` also parses.
    return /^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/.test(text)
      || /^[+-]?(?:inf|infinity|nan)$/i.test(text);
  },

  getTableInput: ({defaultValue, onChange, onImmediatelyChange, propName, placeholderPropName, record, suggestionPropName, t, ...rest}) => {
    const { suggestion, hasSuggestionValue } = accountingHelpers.getSuggestionState({
      defaultValue,
      propName,
      record,
      suggestionPropName
    });
    const placeholder = t?.('placeholderTemplates.enter', {name: t?.(placeholderPropName || propName.replaceAll('_', ' ')).toLowerCase()});
    const inputProps = {
      debounceTime: 2000,
      defaultValue,
      placeholder,
      variant: 'borderless',
      ...rest
    };

    if (hasSuggestionValue) {
      return (
        <SuggestionTableInput
          {...inputProps}
          onChange={onChange}
          onImmediatelyChange={onImmediatelyChange}
          propName={propName}
          record={record}
          suggestion={suggestion}
        />
      );
    }
    return (
      <StyledJournalEntriesTabsInput
        {...inputProps}
        onChange={onImmediatelyChange}
        onDebouncedChange={(value) => onChange?.(value)}
      />
    );
  },

  /**
   * Returns a searchable borderless select component for use inside a journal entries table cell.
   * @param {Object} params - Parameters object.
   * @param {*} params.defaultValue - Default selected value.
   * @param {Function} params.onSelect - Callback invoked when an option is selected.
   * @param {Array<{label: string, value: *}>} params.options - Options list for the select.
   * @param {string} params.propName - Field name used to build the placeholder.
   * @param {string} [params.placeholderPropName] - Override for the placeholder translation key.
   * @param {Object} params.record - Table row record.
   * @param {Function} params.t - i18n translation function.
   * @returns {React.ReactElement} Styled select element.
   * @example
   * accountingHelpers.getTableSelect({ defaultValue: 'opt1', onSelect: fn, options, propName: 'class', record, t });
   */
  getTableSelect: ({defaultValue, onSelect, options, propName, placeholderPropName, record, suggestionPropName, t, ...rest}) => {
    const { suggestion, hasSuggestionValue } = accountingHelpers.getSuggestionState({
      defaultValue,
      propName,
      record,
      suggestionPropName
    });
    const placeholder = t?.('placeholderTemplates.select', {name: t?.(placeholderPropName || propName.replaceAll('_', ' ')).toLowerCase()});
    const selectProps = {
      defaultValue: defaultValue || undefined,
      filterOption: (input, option) => option?.label?.toLowerCase().includes(input.toLowerCase()),
      onSelect: (value) => onSelect?.(value),
      options,
      placeholder,
      popupMatchSelectWidth: false,
      showSearch: true,
      style: { width: '100%', maxWidth: 150 },
      styles: { popup: { root: { minWidth: 150, maxWidth: 400 } } },
      ...rest
    };

    if (hasSuggestionValue) {
      return (
        <SuggestionTableSelect
          {...selectProps}
          key={defaultValue}
          propName={propName}
          record={record}
          suggestion={suggestion}
          variant='borderless'
        />
      );
    }
    return (
      <StyledJournalEntriesTabsSelect
        {...selectProps}
        key={defaultValue}
        onClick={(e) => e.stopPropagation()}
        variant='borderless'
      />
    );
  },

  /**
   * Returns a borderless date picker component for use inside a journal entries table cell.
   * @param {Object} params - Parameters object.
   * @param {string} params.defaultValue - Default date value in 'YYYY-MM-DD' format.
   * @param {Function} params.onChange - Callback invoked with the selected date string.
   * @param {string} params.propName - Field name (kept for consistency with other table helpers).
   * @param {Object} params.record - Table row record.
   * @param {Function} params.t - i18n translation function.
   * @returns {React.ReactElement} Styled date picker element.
   * @example
   * accountingHelpers.getTableDateSelector({ defaultValue: '2024-01-15', onChange: fn, propName: 'posting_date', record, t });
   */
  getTableDateSelector: ({defaultValue, onChange, propName, record, t, ...rest}) => (
    <div className='d-flex' onClick={(e) => e.stopPropagation()}>
      <StyledJournalEntriesTabsDatePicker
        allowClear={false}
        defaultValue={defaultValue ? dayjs(defaultValue, 'YYYY-MM-DD') : null}
        format='YYYY-MM-DD'
        key={defaultValue}
        onChange={(_, dateString) => dateString && onChange?.(dateString)}
        variant='borderless'
        {...rest}
      />
    </div>
  ),

  /**
   * Returns a StatusMark element for the journal entry sync status, wrapped in a Tooltip on error.
   * @param {*} _ - Unused cell value parameter.
   * @param {Object} data - Journal entry data with sync_status and sync_message.
   * @returns {React.ReactElement} StatusMark, optionally wrapped in an error Tooltip.
   * @example
   * accountingHelpers.getSyncStatus(null, { sync_status: 'ERROR', sync_message: 'Failed' });
   */
  getSyncStatus: (_, data) => {
    const { sync_status: statusCode, sync_message: syncMessage } = data || {};
    const statusMark = <StatusMark statuses={journalEntrySyncStatuses} statusCode={statusCode} />;
    return statusCode === journalEntrySyncStatusConstants.ERROR
      ? <Tooltip title={syncMessage}>{statusMark}</Tooltip>
      : statusMark;
  },

  /**
   * Returns the vendor name element, showing a MissingInfo component when the vendor does not exist or is not synced.
   * @param {Object} [params={}] - Parameters object.
   * @param {boolean} [params.exist=true] - Whether the vendor record exists.
   * @param {Object} params.data - Record object containing a vendor property.
   * @param {Function} params.t - i18n translation function.
   * @returns {React.ReactElement|string} Vendor name element or MissingInfo component.
   * @example
   * accountingHelpers.getVendorName({ exist: true, data: { vendor: { name: 'Acme' } }, t });
   */
  getVendorName: ({exist = true, data, t, isExportMode = false} = {}) => {
    if (!exist) return <MissingInfo description={`${t('processing')} ${t('vendor')}`} />;

    // The vendor object is merged into the row client-side once the batch vendor fetch resolves
    // (see updateTableDataWithVendors), so a row that genuinely has a vendor can render before its
    // vendor arrives. Falling through here would mark an existing vendor as "new" with a blank
    // name; render nothing until the merge lands instead (H1MVP-4464).
    const vendor = data?.vendor;
    if (!vendor) return null;

    // ⚠ NO "NEW" BADGE IN EXPORT MODE (RG8, Enric round 4): "Remove 'New' validation on Vendor and
    // Vendor Bills. This is not needed on this Accounting Mode as there's no ERP connected."
    //
    // The badge means "this vendor does not exist in the ERP yet, so registering will create it",
    // and it is decided by `accounting_external_reference` — an id the ERP issues. A manual company
    // has no ERP and no such id, so EVERY vendor carries it, on every row, forever. A flag that is
    // always on tells the user nothing and trains them to ignore it.
    if (isExportMode) return vendor.name;

    return vendorsHelpers.isVendorAccountingReference(vendor)
      ? vendor.name
      : (
        <Space size='small' direction='horizontal'>
          <MissingInfo description={t?.('new')} />
          <span>{vendor.name}</span>
        </Space>
      );
  },

  /**
   * Returns the vendor name element for a journal entry row, showing a placeholder when the expense is not invoice-type.
   * @param {Object} [params={}] - Parameters object.
   * @param {Object} params.record - Journal entry record with expense and vendor_id properties.
   * @param {Function} params.t - i18n translation function.
   * @returns {React.ReactElement|string} Vendor name element, MissingInfo component, or a 'noVendor' placeholder span.
   * @example
   * accountingHelpers.getJEVendorName({ record: entry, t });
   */
  getJEVendorName: ({record, t, isExportMode = false} = {}) => {
    if (!invoicesHelpers.isExpenseInvoiceType(record?.expense)) {
      return <span className='placeholder-text'>{t('noVendor')}</span>;
    }
    return accountingHelpers.getVendorName({exist: Boolean(record.vendor_id), data: record, t, isExportMode});
  },

  /**
   * Returns the document-type cell element for a journal entry table row.
   * Renders the expense type icon and label, optional invoice link icons, a remove-attachment button,
   * and a multi-invoice warning icon for non-receipt entries.
   * @param {Object} [params={}] - Parameters object.
   * @param {Object} params.record - Journal entry record with expense, invoice_ids, and vendor_id.
   * @param {Function} params.t - i18n translation function (main namespace).
   * @param {Function} [params.onAttachmentRemove] - When provided, shows a remove-attachment button and invokes this callback with (event, recordId) on click. Omit to hide the button entirely.
   * @param {boolean} [params.showMissingBill=true] - When false, suppresses the 'new' MissingInfo label for entries with a missing vendor bill.
   * @returns {React.ReactElement} Document cell element.
   * @example
   * accountingHelpers.getJEDocumentCell({ record, t, onAttachmentRemove: handleRemove });
   * accountingHelpers.getJEDocumentCell({ record, t, showMissingBill: false }); // history view
   */
  getJEDocumentCell: ({record, t, onAttachmentRemove, showMissingBill = true, isExportMode = false} = {}) => {
    if (!record || !record.expense) return '';
    const isReceiptExpense = record?.expense?.expense_type === expenseTypesConstants.RECEIPT;
    const invoiceIds = record?.invoice_ids || [];
    const hasAttachments = Boolean(invoiceIds.length);
    const docType = expensesHelpers.getExpenseTypeIconWithLabel(record?.expense, t);
    const isInvoiceRequested = record?.invoice_requested === true || record?.expense?.invoice_requested === true;
    const showMissingBillLabel = showMissingBill && accountingHelpers.isMissingInvoice(record, isExportMode) && !isReceiptExpense;

    const removeAttachmentIcon = (
      <Tooltip title={`${t?.('remove')} ${t?.('attachment')}`}>
        <StyledMissingErpTabIcon onClick={(e) => { e.stopPropagation(); onAttachmentRemove?.(e, record.id); }}>
          <CloseIcon />
        </StyledMissingErpTabIcon>
      </Tooltip>
    );

    return (
      <Space size='small' wrap={false}>
        <Space size='small' className='d-flex d-flex-child' wrap={false} style={{minWidth: 130}}>
          {accountingHelpers.renderJEInvoicePreview(record, docType?.icon) || <StyledJournalEntriesTabsIconSpace>{docType?.icon}</StyledJournalEntriesTabsIconSpace>}
          <Space size='small' className='d-flex d-flex-child' wrap={false}>
            {isInvoiceRequested && <MissingInfo description={t?.('invoiceRequested')} />}
            {!isInvoiceRequested && showMissingBillLabel ? <MissingInfo description={t?.('new')} /> : ''}
            <span>{docType?.label}</span>
          </Space>
        </Space>
        {invoiceIds.map((id, key) => accountingHelpers.getInvoiceLinkIcon({id, t, key}))}
        {hasAttachments && onAttachmentRemove && removeAttachmentIcon}
      </Space>
    );
  },

  /**
   * Returns true if all required custom segment fields have non-empty values on the given record.
   * @param {Object} [params={}] - Parameters object.
   * @param {Object} params.data - The record to check.
   * @param {Array<Object>} [params.customSegments=[]] - Custom segment definitions with setup metadata.
   * @param {string} [params.linesPropName='lines'] - Property name for the lines array.
   * @param {string} [params.levelsPropName=''] - Key inside segment setup to read levels from.
   * @returns {boolean} True if all segments have values, false otherwise.
   * @example
   * accountingHelpers.allCustomSegmentsFilled({ data: entry, customSegments, levelsPropName: 'je' });
   */
  allCustomSegmentsFilled: ({data, customSegments = [], linesPropName = 'lines', levelsPropName = ''} = {}) => {
    if (customSegments.length) {
      const values = customSegments.map(segment =>
        accountingHelpers.getValue({
          linesPropName, levels: segment?.setup?.[levelsPropName], name: segment?.setup?.internal_id, record: data
        })
      );
      return values.every(v => v !== null && v !== undefined && v !== '');
    }
    return true;
  },

  /**
   * Returns true if a journal entry meets all requirements to be synced to the ERP.
   * @param {Object} data - Journal entry data object.
   * @param {Array<Object>} [customSegments=[]] - Custom segment definitions to validate.
   * @returns {boolean} True if the entry is ready to sync.
   * @example
   * accountingHelpers.isEntryReadyToSync(entry, customSegments);
   */
  isEntryReadyToSync: (data, customSegments = []) => {
    const status = data?.status;
    if (![READY_FOR_SUBMISSION, READY_FOR_REVIEW].includes(status)) return false;
    // The backend already checked the header's own fields and said what is missing.
    if (isAggregateHeader(data)) return isHeaderReadyToSync(data);

    const requiredFields = [];
    const hasRequiredFields = objectHelpers.areAllRequiredFieldsFilled(data, requiredFields);
    if (!hasRequiredFields) return false;

    const allCustomSegmentsFilled = accountingHelpers.allCustomSegmentsFilled({
      data, customSegments, linesPropName: 'lines', levelsPropName: customSegmentLevelTypeConstants.JE
    });
    if (!allCustomSegmentsFilled) return false;

    const hasDebit = accountingHelpers.getDebitAccount(data);
    const hasCredit = accountingHelpers.getCreditAccount(data);
    const hasVendor = data?.expense ? data?.expense?.expense_type === expenseTypesConstants.INVOICE ? vendorsHelpers.isVendorAccountingReference(data?.vendor) : true : false;
    const hasInvoice = !accountingHelpers.isMissingInvoice(data);
    return [hasDebit, hasCredit, hasVendor, hasInvoice].every(Boolean);
  },

  /**
   * Returns true if a vendor bill invoice meets all requirements to be synced to the ERP.
   * @param {Object} params - Parameters object.
   * @param {Object} params.data - Vendor bill data object.
   * @param {Array<Object>} [params.customSegments=[]] - Custom segment definitions to validate.
   * @param {boolean} [params.showAccountFields=true] - When false the Payables/Expense account fields are not shown, so they are not required for sync.
   * @param {boolean} [params.showListItem=true] - When false the List Item field is not shown, so it is not required for sync.
   * @param {boolean} [params.requireNumberingFlow=true] - When false (IAL) the Numbering Flow is not used, so it is not required for sync.
   * @returns {boolean} True if the invoice is ready to sync.
   * @example
   * accountingHelpers.isInvoiceReadyToSync({ data: invoice, customSegments, showAccountFields, showListItem, requireNumberingFlow });
   */
  isInvoiceReadyToSync: ({data, customSegments = [], showAccountFields = true, showListItem = true, requireNumberingFlow = true} = {}) => {
    const requiredFields = [
      ...(requireNumberingFlow ? ['customrecord_nexil_numbflow'] : []),
      ...(showAccountFields ? ['payables_account', 'expenses_account'] : []),
    ];
    const hasRequiredFields = objectHelpers.areAllRequiredFieldsFilled(data, requiredFields);
    if (!hasRequiredFields) return false;

    const allCustomSegmentsFilled = accountingHelpers.allCustomSegmentsFilled({
      data, customSegments, linesPropName: 'item', levelsPropName: customSegmentLevelTypeConstants.VENDOR_BILL
    });
    if (!allCustomSegmentsFilled) return false;

    const hasListItem = !showListItem || Boolean(accountingHelpers.getBillListItem(data));
    const hasTaxCode = accountingHelpers.getBillTaxCode(data);
    const hasVendor = vendorsHelpers.isVendorAccountingReference(data?.vendor);
    return [hasListItem, hasVendor, hasTaxCode, hasRequiredFields].every(Boolean);
  },

  /**
   * Returns true if a vendor record has all required fields filled for ERP sync.
   * @param {Object} data - Vendor data object.
   * @param {Object} [options={}] - Options object.
   * @param {boolean} [options.requireNumberingFlow=true] - When false (IAL) the vendor has no Numbering Flow and no tax code, so neither is required for sync.
   * @returns {boolean} True if the vendor is ready to sync.
   * @example
   * accountingHelpers.isVendorReadyToSync({ tax_number: 'IT123', country: 'IT', address: 'Via Roma 1', vendor_tax_code: 'ABC', default_numbering_flow: 'flow-1' });
   */
  isVendorReadyToSync: (data, {requireNumberingFlow = true} = {}) => {
    const requiredFields = ['tax_number', 'country', 'address',
      ...(requireNumberingFlow ? ['vendor_tax_code', 'default_numbering_flow'] : []),
      'default_expense_account', 'default_payables_account'];
    return objectHelpers.areAllRequiredFieldsFilled(data, requiredFields);
  },

  /**
   * Enriches an invoice with vendor and uploader info, then computes its is_ready_to_sync flag.
   * @param {Object} [params={}] - Parameters object.
   * @param {Array<Object>} [params.customSegments=[]] - Custom segment definitions.
   * @param {Object} params.invoice - Raw invoice object.
   * @param {Array<Object>} [params.employees=[]] - Employee list for resolving uploader.
   * @param {Array<Object>} [params.vendors=[]] - Vendor list for resolving vendor reference.
   * @param {boolean} [params.showAccountFields=true] - When false the Payables/Expense account fields are hidden and not required for sync.
   * @param {boolean} [params.showListItem=true] - When false the List Item field is hidden and not required for sync.
   * @param {boolean} [params.requireNumberingFlow=true] - When false (IAL) the Numbering Flow is not required for sync.
   * @returns {Object} Enriched invoice object with is_ready_to_sync flag.
   * @example
   * accountingHelpers.formatInvoice({ invoice, employees, vendors, customSegments, showAccountFields, showListItem, requireNumberingFlow });
   */
  formatInvoice: ({customSegments = [], invoice, employees = [], vendors = [], showAccountFields = true, showListItem = true, requireNumberingFlow = true} = {}) => {
    invoice = {
      ...invoice,
      vendor: vendors.find((v) => v.vendor_id === invoice.vendor_id),
    }
    if (!invoice.uploaded_by) {
      invoice = {
        ...invoice,
        uploaded_by: employees.find(e => e.employee_id === invoice?.uploaded_by_user_id)?.email || '',
      }
    }

    return {
      ...invoice,
      _record_type: suggestionRecordTypeConstants.VENDOR_BILL,
      is_ready_to_sync: accountingHelpers.isInvoiceReadyToSync({data: invoice, customSegments, showAccountFields, showListItem, requireNumberingFlow}),
      ...(invoice.sdi_lines?.length
        ? { sdi_lines: invoice.sdi_lines.map((line) =>
            accountingHelpers.formatInvoice({ customSegments, invoice: line, vendors, employees, showAccountFields, showListItem, requireNumberingFlow })) }
        : {}),
    };
  },

  /**
   * Removes the given ids from the top-level rows and from any nested sdi_lines groups.
   * Used when an sdi_lines item is deleted or synced so it disappears from its group.
   * When a parent row is removed but some of its sdi_lines survive, the survivors are
   * promoted to top-level rows so pending line items are never silently dropped.
   * @param {Object} params - Parameters object.
   * @param {Array<Object>} [params.rows=[]] - Invoice rows, each optionally carrying an sdi_lines array.
   * @param {Array<string>|Set<string>} [params.ids=[]] - Ids to remove.
   * @returns {Array<Object>} New rows array with matching top-level rows and nested sdi_lines removed.
   * @example
   * accountingHelpers.removeInvoiceRowsByIds({ rows, ids: ['abc'] });
   */
  removeInvoiceRowsByIds: ({ rows = [], ids = [] }) => {
    const idSet = ids instanceof Set ? ids : new Set(ids);
    return rows.reduce((acc, row) => {
      const survivingLines = (row.sdi_lines || []).filter((line) => !idSet.has(line.id));
      if (idSet.has(row.id)) {
        acc.push(...survivingLines);
        return acc;
      }
      acc.push(row.sdi_lines?.length ? { ...row, sdi_lines: survivingLines } : row);
      return acc;
    }, []);
  },

  /**
   * Enriches a journal entry with expense, invoice, and vendor references, then computes derived account and sync fields.
   * @param {Object} params - Parameters object.
   * @param {Array<Object>} [params.customSegments=[]] - Custom segment definitions.
   * @param {Object} params.entry - Raw journal entry object.
   * @param {Object} [params.expensesMap={}] - Map of expenses keyed by expense_id.
   * @param {Object} [params.invoicesMap={}] - Map of invoices keyed by invoice_id.
   * @param {Array<Object>} [params.vendors=[]] - Vendor list for resolving vendor reference.
   * @returns {Object} Enriched journal entry with credit_account, debit_account and is_ready_to_sync.
   * @example
   * accountingHelpers.formatJournalEntry({ entry, expensesMap, invoicesMap, vendors, customSegments });
   */
  formatJournalEntry: ({customSegments = [], entry, expensesMap = {}, invoicesMap = {}, vendors = []}) => {
    entry = {
      ...entry,
      expense: expensesMap[entry.expense_id] || null,
      invoice: invoicesMap[entry.invoice_id] || null,
      vendor: vendors.find((v) => v.vendor_id === entry.vendor_id),
    }
    const isHeader = isAggregateHeader(entry);
    const formatted = {
      ...entry,
      _record_type: suggestionRecordTypeConstants.JOURNAL_ENTRY,
      credit_account: isHeader ? getHeaderCreditAccount(entry) : accountingHelpers.getCreditAccount(entry),
      debit_account: isHeader ? null : accountingHelpers.getDebitAccount(entry),
      is_ready_to_sync: accountingHelpers.isEntryReadyToSync(entry, customSegments)
    };
    if (!isHeader) return formatted;

    const inheritLocation = !isLocationLineLevel({customSegments, levelsPropName: customSegmentLevelTypeConstants.JE});
    const members = getMembers(formatted).map(member => formatMember({
      member,
      header: formatted,
      expense: expensesMap[member.expense_id] || null,
      vendor: vendors.find(v => String(v.vendor_id) === String(member.vendor_id)),
      inheritLocation,
    }));
    return {...formatted, children: getDisplayChildren(formatted, members)};
  },

  /**
   * Returns a vendor object enriched with an is_ready_to_sync flag.
   * @param {Object} vendor - Raw vendor object.
   * @param {Object} [options={}] - Options object.
   * @param {boolean} [options.requireNumberingFlow=true] - When false (IAL) the Numbering Flow and tax code are not required for sync.
   * @returns {Object} Vendor object with is_ready_to_sync property.
   * @example
   * accountingHelpers.formatVendor(vendor, { requireNumberingFlow });
   */
  formatVendor: (vendor, {requireNumberingFlow = true} = {}) => {
    return {
      ...vendor,
      _record_type: suggestionRecordTypeConstants.VENDOR,
      is_ready_to_sync: accountingHelpers.isVendorReadyToSync(vendor, {requireNumberingFlow})
    };
  },

  /**
   * Returns true if the bill invoice sync status is ERROR.
   * @param {string} status - Sync status string.
   * @returns {boolean} True if status equals the ERROR constant.
   * @example
   * accountingHelpers.isBillInvoiceSyncStatusError('ERROR'); // true
   */
  isBillInvoiceSyncStatusError: (status) => status === billInvoiceSyncStatusConstants.ERROR,

  /**
   * Returns true if the bill invoice sync status is SUCCESS.
   * @param {string} status - Sync status string.
   * @returns {boolean} True if status equals the SUCCESS constant.
   * @example
   * accountingHelpers.isBillInvoiceSyncStatusSuccess('success'); // true
   */
  isBillInvoiceSyncStatusSuccess: (status) => status === billInvoiceSyncStatusConstants.SUCCESS,

  /**
   * Returns true if the journal entry sync status is ERROR.
   * @param {string} status - Sync status string.
   * @returns {boolean} True if status equals the ERROR constant.
   * @example
   * accountingHelpers.isEntrySyncStatusError('ERROR'); // true
   */
  isEntrySyncStatusError: (status) => status === journalEntrySyncStatusConstants.ERROR,

  /**
   * Returns true if the vendor sync status is ERROR.
   * @param {string} status - Sync status string.
   * @returns {boolean} True if status equals the ERROR constant.
   * @example
   * accountingHelpers.isVendorSyncStatusError('ERROR'); // true
   */
  isVendorSyncStatusError: (status) => status === vendorSyncStatusConstants.ERROR,

  /**
   * Dispatches an alert action of the given type with a message, duration, and optional offset.
   * @param {'success'|'warning'|'error'} type - Alert type.
   * @param {string} message - Alert message text.
   * @param {number} [duration=4000] - Alert display duration in milliseconds.
   * @param {number} [offset=350] - Vertical offset for success/warning alerts in pixels.
   * @returns {void}
   * @example
   * accountingHelpers.showAlert('success', 'Items synced successfully');
   */
  showAlert: (type, message, duration = 4000, offset = 350) => {
    if (type === 'success') alertActions.success(message, duration, offset);
    if (type === 'warning') alertActions.warning(message, duration, offset);
    if (type === 'error') alertActions.error(message, duration);
  },

  /**
   * Returns a value from table data for a specific view and key, or a default value if the view is absent.
   * @param {Object} tableData - Object keyed by view names containing per-view table state.
   * @param {string} view - View name key.
   * @param {string} key - Property key within the view's data.
   * @param {*} defaultValue - Value to return when the view is not present.
   * @returns {*} The found value or defaultValue.
   * @example
   * accountingHelpers.getTableDataValue(tableData, 'pending', 'rows', []);
   */
  /**
   * Replace one row inside a rows array, by id, without touching the rest.
   *
   * ⚠ ALWAYS CALL THIS WITH `prevState[view]?.rows`, never with a `tableData` read from the
   * enclosing scope. That distinction is H1MVP-4807: two of the three pages computed the new rows
   * from the captured `tableData` and then wrote them through `setTableData(prev => ...)`, which
   * LOOKS safe and is not — the functional form only protects the spread, while the rows themselves
   * came from a snapshot taken before the request went out. A second edit made while the first was
   * in flight was silently reverted when the first response landed.
   *
   * Also descends into `sdi_lines`, because a Vendor Bill row can carry the edited record as a
   * child, and into `aggregate_members`, the fetched expenses of an aggregated journal entry. Both
   * are no-ops for rows that have no such nesting.
   *
   * @param {Array<Object>} rows
   * @param {string} id
   * @param {Function} updater - `(row, header) => nextRow`, called only for the matched row. `header`
   *   is set when the match is one of an aggregated entry's members.
   * @returns {Array<Object>}
   */
  applyRowUpdate: (rows, id, updater) =>
    (rows || []).map(row => {
      if (row.id === id) return updater(row);
      if (row.sdi_lines?.some(line => line.id === id)) {
        return {...row, sdi_lines: row.sdi_lines.map(line => (line.id === id ? updater(line) : line))};
      }
      if (row.aggregate_members?.some(member => member.id === id)) {
        return {...row, aggregate_members: row.aggregate_members.map(member => (member.id === id ? updater(member, row) : member))};
      }
      return row;
    }),

  getTableDataValue: (tableData, view, key, defaultValue) =>
    tableData.hasOwnProperty(view) ? tableData[view][key] : defaultValue,

  /**
   * Builds the common props object passed to an accounting tab component including data, pagination, and event handlers.
   * @param {Object} params - Parameters object.
   * @param {string} params.key - View/tab key.
   * @param {string} params.totalKey - Key used to read the total count from the total object.
   * @param {Object} params.tableData - Table data state keyed by view.
   * @param {Object} params.expensesMap - Map of expenses keyed by expense_id.
   * @param {Object} params.loading - Loading state keyed by view.
   * @param {Function} params.handleRowUpdate - Row update handler function.
   * @param {Function} params.handleOnTableChange - Table change handler function.
   * @param {Function} params.handleOnPaginate - Pagination handler function.
   * @param {Object} params.total - Object containing total counts keyed by view.
   * @param {Object} [params.queries={}] - Filter queries keyed by view; the view's query is passed down as `query`.
   * @param {Object} [params.tableProps={}] - Additional props merged into tableProps.
   * @returns {Object} Props object for an accounting tab component.
   * @example
   * accountingHelpers.getTabProps({ key: 'pending', tableData, loading, handleRowUpdate, handleOnTableChange, handleOnPaginate, total, totalKey: 'pending' });
   */
  getTabProps: ({
    key,
    totalKey,
    tableData,
    expensesMap,
    loading,
    handleRowUpdate,
    handleOnTableChange,
    handleOnPaginate,
    queries = {},
    dateQuery = null,
    total,
    tableProps = {},
    ...rest
  }) => ({
    data: accountingHelpers.getTableDataValue(tableData, key, 'rows', []),
    // The filter bar lives in the tab bar now, so the tab needs the query for one thing only: its
    // empty state, which asks whether ANYTHING is filtering (useEmptyDetails).
    //
    // ⚠ The date range has to be handed over explicitly. It is the filter people reach for most on
    // these pages and the one they can see as a chip, but it is held by useAccountingDateRangeFilter
    // rather than in the tab's stored query — so without this a period that empties the list reads
    // as "there is nothing here" instead of "nothing in this period".
    query: {...(queries[key] || {}), ...(dateQuery || {})},
    loading: loading[key] || false,
    onRowUpdate: (props) => handleRowUpdate({...props, view: key}),
    tableProps: {
      onChange: (pagination, filters, sorter, extra) => handleOnTableChange(key, pagination, filters, sorter, extra),
      pagination: tabHelpers.getTablePagination({handleOnPaginate, tableData, total, totalKey, view: key}),
      ...tableProps,
    },
    ...rest
  }),

  /**
   * Builds the props object for a journal entries tab, formatting each entry before passing to getTabProps.
   * @param {Object} params - Parameters object.
   * @param {Array<Object>} [params.customSegments=[]] - Custom segment definitions.
   * @param {string} params.key - View/tab key.
   * @param {Object} [params.expensesMap={}] - Map of expenses keyed by expense_id.
   * @param {Object} [params.invoicesMap={}] - Map of invoices keyed by invoice_id.
   * @param {Object} params.tableData - Table data state keyed by view.
   * @param {Array<Object>} params.vendors - List of vendor objects.
   * @returns {Object} Props object for a journal entries tab component.
   * @example
   * accountingHelpers.getJournalEntriesTabProps({ key: 'pending', tableData, vendors, expensesMap, invoicesMap });
   */
  getJournalEntriesTabProps: ({
    customSegments = [],
    key,
    expensesMap = {},
    invoicesMap = {},
    tableData,
    vendors,
    ...rest
  }) => {
    const rows = accountingHelpers.getTableDataValue(tableData, key, 'rows', [])
      .map(entry => accountingHelpers.formatJournalEntry({
        customSegments, entry, expensesMap, invoicesMap, vendors
      }));

    return accountingHelpers.getTabProps({
      key,
      tableData: { ...tableData, [key]: { ...tableData[key], rows } },
      ...rest
    });
  },

  /**
   * Builds the props object for an invoices tab, formatting each invoice before passing to getTabProps.
   * @param {Object} params - Parameters object.
   * @param {Array<Object>} [params.customSegments=[]] - Custom segment definitions.
   * @param {string} params.key - View/tab key.
   * @param {Object} params.tableData - Table data state keyed by view.
   * @param {Array<Object>} [params.employees=[]] - List of employee objects.
   * @param {Array<Object>} [params.vendors=[]] - List of vendor objects.
   * @param {boolean} [params.showAccountFields=true] - When false the Payables/Expense account fields are hidden and not required for sync.
   * @param {boolean} [params.showListItem=true] - When false the List Item field is hidden and not required for sync.
   * @param {boolean} [params.requireNumberingFlow=true] - When false (IAL) the Numbering Flow is not required for sync.
   * @returns {Object} Props object for an invoices tab component.
   * @example
   * accountingHelpers.getInvoicesTabProps({ key: 'missing', tableData, employees, vendors, showAccountFields, showListItem, requireNumberingFlow });
   */
  getInvoicesTabProps: ({
    customSegments = [],
    key,
    tableData,
    employees = [],
    vendors = [],
    showAccountFields = true,
    showListItem = true,
    requireNumberingFlow = true,
    ...rest
  }) => {
    let rows = accountingHelpers.getTableDataValue(tableData, key, 'rows', []).map(invoice =>
      accountingHelpers.formatInvoice({ customSegments, invoice, vendors, employees, showAccountFields, showListItem, requireNumberingFlow })
    );

    return accountingHelpers.getTabProps({
      key,
      tableData: { ...tableData, [key]: { ...tableData[key], rows } },
      ...rest
    });
  },

  /**
   * Returns the row selection configuration object for an accounting table. Rows nested under an
   * aggregated journal entry have no checkbox: only the header is selected.
   * @param {Object} params - Parameters object.
   * @param {Function} params.onChange - Callback invoked with updated selected row keys.
   * @param {string|undefined} params.syncStatus - When defined, disables rows whose sync_status differs.
   * @param {Array<string>} [params.selectedRowKeys=[]] - Currently selected row keys.
   * @param {boolean} [params.allowMembers=false] - Whether the expenses of a header can be ticked.
   * @param {'members'|'rows'|null} [params.selectionKind=null] - What is selected now; the other kind is disabled.
   * @returns {{getCheckboxProps: Function, selectedRowKeys: Array<string>, onChange: Function}} Row selection config.
   * @example
   * accountingHelpers.getTableRowSelection({ onChange: fn, syncStatus: 'SUCCESS', selectedRowKeys: [] });
   */
  getTableRowSelection: ({ onChange, syncStatus, selectedRowKeys = [], noRestrictions = false, allowMembers = false, selectionKind = null }) => ({
    getCheckboxProps: (record) => {
      if (isAggregateMember(record)) {
        return {disabled: !allowMembers || !isSelectableMember(record) || selectionKind === 'rows'};
      }
      return {
        disabled: isAggregateChildRow(record) || selectionKind === 'members' || (!noRestrictions && (
          syncStatus !== undefined
            ? record.sync_status !== syncStatus
            : !record.is_ready_to_sync
        )),
      };
    },
    // The note and the placeholders have no checkbox. A member has one only where its expenses
    // can be classified in bulk, and the selection never mixes members with other rows.
    renderCell: (_checked, record, _index, originNode) => (
      isAggregateChildRow(record) && !(allowMembers && isAggregateMember(record)) ? null : originNode
    ),
    selectedRowKeys,
    onChange: (rows) => onChange?.(rows),
  }),

  /**
   * Returns true when a journal entry has an associated vendor but lacks a valid linked accounting invoice for an INVOICE-type expense.
   * An accounting invoice is considered valid when it has a remote_id and its vendor_bill.sync_status is success or null/undefined.
   * @param {Object} record - Journal entry record.
   * @returns {boolean} True if the invoice is missing.
   * @example
   * accountingHelpers.isMissingInvoice(entry);
   */
  isMissingInvoice: (record, isExportMode = false) => {
    const isExpenseType = record?.expense?.expense_type === expenseTypesConstants.INVOICE;
    // ⚠ `remote_id` IS THE ERP'S OWN ID, so in export mode it is never issued and this was always
    // false — labelling every invoice-type row with a vendor as missing its bill, including rows
    // whose bill is plainly attached (Enric round 4: rows showing "New" next to the link and
    // remove-attachment icons for the very invoice they were said to be missing). Measured on dev:
    // `invoice_ids: ["e691959b-…"]`, `vendor_bill` present, `remote_id: null`.
    // A manual company's bill is linked or it is not; the ERP has no opinion to wait for.
    const isAccountingInvoice = isExportMode
      ? Boolean(record?.vendor_bill)
      : Boolean(record?.vendor_bill?.remote_id);
    const syncStatus = record?.vendor_bill?.sync_status;
    const hasValidSyncStatus = syncStatus == null || syncStatus === journalEntrySyncStatusConstants.SUCCESS;
    return record?.vendor_id && isExpenseType && !(isAccountingInvoice && hasValidSyncStatus);
  },

  /**
   * Returns true if the journal entry record has more than one attached invoice.
   * @param {Object} record - Journal entry record.
   * @returns {boolean}
   * @example
   * accountingHelpers.hasMultipleInvoices(entry);
   */
  hasMultipleInvoices: (record) => record?.has_multiple_vendor_bills,

  /**
   * Returns a Tooltip-wrapped check icon when the record is ready to sync, otherwise an empty string.
   *
   * ⚠ NOT SHOWN ON A RECORD THAT IS ALREADY REGISTERED (RG1, Enric round 4): "we are still showing
   * 'ready to sync' icon for registered items, this icon should never be visible in the Registered
   * tab". The service keeps returning `is_ready_to_sync: true` after a row is registered — the flag
   * describes the row's completeness, not its destination — so a tick promising future work sat on
   * rows where the work was already done.
   *
   * Decided about the RECORD rather than about the tab, on the same reasoning as
   * `useAccountingRegister.isRegisterable`: a registered row is then safe to render from anywhere,
   * and no caller has to remember which tab it is on. Both markers are checked because the two modes
   * stamp different ones — export mode a `wl_reference`, NetSuite a `sync_status` of success.
   *
   * @param {Object} record - Journal entry, vendor or invoice record.
   * @returns {React.ReactElement|string} Check icon element or empty string.
   * @example
   * accountingHelpers.renderReadyToSyncIcon({ is_ready_to_sync: true });
   */
  renderReadyToSyncIcon: (record) => isAggregateHeader(record) && record.blocking_reason && !accountingHelpers.isRegisteredRecord(record) ? (
    <Tooltip title={record.blocking_reason}>
      <span aria-label={record.blocking_reason} className='d-flex' role='img' tabIndex={0}>
        <IconWrapper color={colorsStyles.functional_warning} size={24}>
          <AlertTriangleIcon />
        </IconWrapper>
      </span>
    </Tooltip>
  ) : record?.is_ready_to_sync && !accountingHelpers.isRegisteredRecord(record) ? (
    <ScaleFade className='d-flex'>
      <Tooltip title='Ready to sync'>
        <IconWrapper color={colorsStyles.neutral_7} size={24}>
          <CheckIcon />
        </IconWrapper>
      </Tooltip>
    </ScaleFade>
  ) : '',

  /**
   * Has this record already been registered (export mode) or synced (NetSuite)?
   *
   * One predicate rather than each caller remembering that the two modes stamp different markers:
   * export mode writes a `wl_reference`, which is FINAL and has no un-register; NetSuite moves
   * `sync_status` to success. A caller that checked only one would be right in one mode and wrong in
   * the other, which is how the ready-to-sync tick survived on registered rows (RG1).
   *
   * The bill enum is referenced for the success literal because all three — invoice, journal entry
   * and vendor — declare it as `'success'`. One reference rather than a three-way check that would
   * read as thoroughness while testing the same string three times.
   *
   * @param {Object} record
   * @returns {boolean}
   */
  isRegisteredRecord: (record) => Boolean(
    record?.wl_reference || record?.sync_status === billInvoiceSyncStatusConstants.SUCCESS,
  ),

  /**
   * Returns a Space element with an invoice preview icon and translated 'Invoice' label, or empty string when no value.
   * @param {Object} [params={}] - Parameters object.
   * @param {string|number} [params.value] - Invoice ID; returns empty string if falsy.
   * @param {'backend'|string} [params.variant='backend'] - Preview variant passed to InvoicePreviewWithIcon.
   * @param {Function} [params.t] - i18n translation function used to render the label.
   * @returns {React.ReactElement|string} Space with FileInvoiceIcon preview and label, or empty string.
   * @example
   * accountingHelpers.renderInvoicePreview({ value: 'inv-123', variant: 'backend', t });
   */
  renderInvoicePreview: ({value, variant = 'backend', t} = {}) => value ? (
    <Space className='d-flex d-flex-child' size='small'>
      <InvoicePreviewWithIcon
        invoiceId={value}
        variant={variant}
        icon={<IconWrapper size={16}><FileInvoiceIcon /></IconWrapper>}
      />
      <span>{t?.('invoice')}</span>
    </Space>
  ) : '',

  /**
   * Returns an invoice preview element for a journal entry, preferring invoice_id over invoice_file_url.
   * @param {Object|null} journalEntry - JE object with invoice_id or invoice_file_url.
   * @returns {React.ReactElement|string} Preview element or empty string.
   * @example
   * accountingHelpers.renderJEInvoicePreview({ invoice_id: 'inv-1' });
   */
  renderJEInvoicePreview: (journalEntry, icon) => {
    if (!journalEntry) return '';

    const invoiceIds = journalEntry?.invoice_ids;

    if (invoiceIds?.length) {
      return (
        <StyledJournalEntriesTabsIconSpace>
          <InvoicePreviewWithIcon invoiceIds={invoiceIds} icon={icon} />
        </StyledJournalEntriesTabsIconSpace>
      )
    }

    return '';
  },

  /**
   * Initiates the sync operation for a list of item IDs, handling loading state and showing success/error alerts.
   * @param {Object} params - Parameters object.
   * @param {Array<string>} [params.ids=[]] - List of item IDs to sync.
   * @param {Function} params.loadData - Function to reload data after successful sync.
   * @param {Function} params.onSync - Function that performs the sync API call.
   * @param {Function} [params.onSuccess] - Optional callback invoked on success before reloading data.
   * @param {Function} params.setSyncButtonLoading - State setter for the sync button loading state.
   * @param {Function} params.trans - i18n translation function.
   * @returns {void}
   * @example
   * accountingHelpers.handleSyncItems({ ids: ['1', '2'], loadData, onSync, setSyncButtonLoading, trans });
   */
  handleSyncItems: ({ids = [], loadData, onSync, onSuccess, setSyncButtonLoading, trans}) => {
    if (!ids.length) {
      accountingHelpers.showAlert('warning', trans('validation.noItemsReadyToSync'));
      return;
    }

    setSyncButtonLoading(true);

    onSync({
      ids,
      successCallback: (response = {}) => {
        if (!response.success) {
          accountingHelpers.showAlert('error', response.message || trans('validation.failedToSendItemsToTheErp'));
        } else {
          onSuccess?.();
          loadData({ initial: true, view: 'synced' });
          accountingHelpers.showAlert('success', trans('validation.itemsHaveBeenSentToErp'));
        }
        setSyncButtonLoading(false);
      },
      errorCallback: (error) => {
        setSyncButtonLoading(false);
        accountingHelpers.showAlert('error', error?.message || trans('validation.failedToSendItemsToTheErp'));
      }
    });
  },

  /**
   * Returns a Tooltip-wrapped external link icon navigating to the invoice details page.
   * @param {Object} [params={}] - Parameters object.
   * @param {string|number} [params.id] - Invoice ID; returns null if falsy.
   * @param {Function} [params.t] - i18n translation function.
   * @returns {React.ReactElement|null} Link icon element or null.
   * @example
   * accountingHelpers.getInvoiceLinkIcon({ id: 'inv-42', t });
   */
  getInvoiceLinkIcon: ({id, t, ...rest} = {}) => {
    if (!id) return null;
    const invoiceUrl = invoicesHelpers.getInvoiceDetailsLink(id);

    return (
      <Tooltip title={t?.('seeAttachment')} {...rest}>
        <Link to={invoiceUrl} onClick={(e) => e.stopPropagation()}>
          <StyledMissingErpTabIcon className='external-link-icon'>
            <ExternalLinkIcon />
          </StyledMissingErpTabIcon>
        </Link>
      </Tooltip>
    )
  },

  /**
   * Returns a Tooltip-wrapped link icon that opens the expense details panel for the row's expense.
   * @param {Object} [params={}] - Parameters object.
   * @param {Object} [params.record] - Journal entry row; returns null when it has no expense_id.
   * @param {Function} [params.t] - i18n translation function.
   * @param {Function} [params.onClick] - Handler invoked with the record when the icon is clicked.
   * @returns {React.ReactElement|null} Link icon element or null.
   * @example
   * accountingHelpers.getExpenseDetailsLinkIcon({ record, t, onClick: onExpenseDetails });
   */
  getExpenseDetailsLinkIcon: ({record, t, onClick} = {}) => {
    if (!record?.expense_id) return null;

    return (
      <Tooltip title={`${t?.('expense')} ${t?.('details')}`}>
        <StyledMissingErpTabIcon
          className='external-link-icon'
          onClick={(e) => {
            e.stopPropagation();
            onClick?.(record);
          }}
        >
          <ExternalLinkIcon />
        </StyledMissingErpTabIcon>
      </Tooltip>
    )
  },

  /**
   * Transforms column definitions into custom segment columns by injecting a select render function.
   * @param {Object} [params={}] - Parameters object.
   * @param {Array<Object>} params.columns - Base column definitions with levels, name, and options.
   * @param {Function} params.getSelect - Function returning a select element for a given cell context.
   * @param {string} [params.linesPropName='lines'] - Property name for the lines array on the record.
   * @returns {Array<Object>} Column definitions with render functions injected.
   * @example
   * accountingHelpers.getCustomSegmentColumns({ columns, getSelect, linesPropName: 'lines' });
   */
  getCustomSegmentColumns: ({columns, getSelect, linesPropName = 'lines'} = {}) =>
    columns.map(({levels, name, options, ...rest}) => ({
      ...rest,
      render: (_, record) => getSelect?.({ defaultValue: accountingHelpers.getValue({ linesPropName, levels, name, record }), levels, record, options, propName: name }),
      width: 150,
      ellipsis: true,
    })),

  /**
   * Re-maps all rows in each tab of the table data using the provided format function when vendors are available.
   * @param {Object} params - Parameters object.
   * @param {Function} params.setTableData - React state setter for the table data.
   * @param {Function} params.formatFunction - Function applied to each row to enrich it with vendor data.
   * @param {Array<Object>} [params.vendors=[]] - List of vendor objects; nothing happens if empty.
   * @returns {void}
   * @example
   * accountingHelpers.updateTableDataWithVendors({ setTableData, formatFunction: accountingHelpers.formatVendor, vendors });
   */
  updateTableDataWithVendors: ({setTableData, formatFunction, vendors = []}) => {
    // If there are no vendors or no table rows, nothing to do
    const hasVendors = vendors && vendors.length > 0;
    if (!hasVendors) return;

    setTableData(prevState => {
      const updatedTabs = Object.keys(prevState).reduce((acc, key) => {
        const tab = prevState[key];
        if (!tab || !Array.isArray(tab.rows)) {
          acc[key] = tab;
          return acc;
        }
        acc[key] = {
          ...tab,
          rows: tab.rows.map(row => formatFunction?.(row) || row)
        };
        return acc;
      }, {});
      return { ...prevState, ...updatedTabs };
    });
  },

  /**
   * Returns default Ant Design Table props shared across accounting tables.
   *
   * @returns {{ pagination: false, scroll: { x: string, y: string } }} Default table props with pagination disabled and scroll configured.
   * @example
   * <Table {...accountingHelpers.getDefaultTableProps()} columns={columns} dataSource={data} />
   */
  getDefaultTableProps: () => ({
    pagination: false,
    scroll: { x: 'max-content', y: 'calc(100vh - 274px)' },
  }),

  /**
   * Returns the expense card payment method label for a record, or an empty string if no card is linked.
   * @param {Object} [params={}] - Parameters object.
   * @param {Object} params.record - Table row record containing an expense object.
   * @param {Function} params.t - i18n translation function.
   * @returns {React.ReactElement|string} Payment method element or empty string.
   * @example
   * accountingHelpers.getExpenseCardNumber({ record: { expense: { card_id: '42', last4: '1234' } }, t });
   */
  getExpenseCardNumber: ({record, t} = {}) => {
    if (!record?.expense?.card_id) return '';
    return expensesHelpers.getExpensePaymentMethod(record.expense, t);
  },

  /**
   * Extracts the human-readable message from a sync error string.
   * The sync API returns Python-style single-quoted dicts (e.g. `"{'code': '400', 'message': '...'}"`)
   * which are not valid JSON. This helper normalises the quotes and returns the `message` field,
   * falling back to the raw string if parsing fails.
   * @param {string} raw - Raw sync_message value from the sync response.
   * @returns {string} The extracted message text, or the original raw string on parse failure.
   * @example
   * accountingHelpers.parseSyncMessage("{'code': '400', 'message': 'Invalid field value'}");
   * // → 'Invalid field value'
   */
  parseSyncMessage: (raw) => {
    try {
      return JSON.parse(raw.replace(/'/g, '"'))?.message || raw;
    } catch {
      return raw;
    }
  },
}
